import React, { useState, useEffect, useMemo } from 'react';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import { fetchJsonCached } from 'components/frank/utils/ManufacturingLineResponseCache.js';
import { pollSectionBestScores } from 'components/frank/utils/sectionBestScorePolling.js';
import { STANDARD } from 'components/frank/utils/manufacturingLinesSource.js';
import { minimumOf } from 'components/utils/minMax';
import {
  manufacturingLineListsFromListing, allManufacturingLinesOf, lineKey,
} from 'components/frank/utils/manufacturingLineLists.js';
import {
  starRatingForScore, Stars, columnVisibilityFor, sectionLabelsFor, maxMachineDimensionOf,
} from 'components/frank/utils/manufacturingLineListRendering.jsx';
import skeletonStyles from 'components/frank/_style.module.scss';

// THE STANDARD MANUFACTURING LINES, shown UNDER the "no manufacturing lines match" finding when the owner is on
// their OWN lines and their search came up empty (see ManufacturingLineLists' renderListing). The owner's account
// makes nothing this section needs, but the shared example account almost always does -- so rather than a dead
// end, the section offers the standard lines and lets the owner borrow one: picking a card flips the whole quote
// to Standard and opens that line's solutions (onSelectStandardLine -> the page's useStandardLineForSection).
//
// It draws the SAME scored cards the standard listing draws -- fetched, scored and rendered here through the same
// shared pieces the main listing uses (fetchJsonCached, pollSectionBestScores, buildManufacturingLineLists, and
// the shared card helpers), just READ-ONLY: no filter island, no re-search, no per-card pick/dim. A card is a
// choice, and the choice is "use this standard line".
//
// STANDARD SOURCE, always: these fetches carry an EXPLICIT manufacturing_lines_source=standard. Omitting it does
// NOT mean standard -- Frank::ManufacturingLinesSource.value falls back to the quote's STORED source column when
// the param is absent, and once the owner has switched the quote to their own lines that column is 'own', so an
// omitted param would re-fetch the owner's OWN (empty) lines and the fallback would show nothing. The owner's
// chosen material (material_user_price_id) is deliberately dropped -- that id is one of the owner's OWN
// UserUnitPrice rows and means nothing to the standard account, so the server auto-picks the standard material.
// The account-independent filters (bobbin, machine-type ticks, GSM, the area-ratio cap, disabled reductions)
// carry over unchanged.

// How many of each part's cheapest survive the cap, the same value the main listing reads (see TOP_N_PER_PART
// there). The server sends a positive integer or its own default, so the literal is only for surfaces that set
// no global (e.g. JS tests).
const TOP_N_PER_PART = window.frank_top_n_per_part ?? 10;

// The listing / best-score filter params shared by both fetches -- the section's account-independent filters,
// with an EXPLICIT standard source and NO material (see the note above).
const buildFilterParams = ({
  bobbinEnabled, disabledFilters, selectedTypes, selectedCartonGsm, areaRatioPercent, count,
}) => {
  const params = new URLSearchParams();
  // Explicit, not omitted: an absent param resolves to the quote's stored source, which is 'own' here.
  params.set('manufacturing_lines_source', STANDARD);
  if (bobbinEnabled !== undefined) { params.set('bobbin_enabled', bobbinEnabled); }
  (disabledFilters || []).forEach((key) => params.append('disabled_filters[]', key));
  Object.entries(selectedTypes || {}).forEach(([key, types]) => {
    // A query string cannot carry an empty array, and "absent" means "use the default" -- so an empty selection
    // is one blank entry, which the server reads as "nothing ticked" (matching the main listing fetch).
    if (types.length === 0) { params.append(`${key}[]`, ''); return; }
    types.forEach((type) => params.append(`${key}[]`, type));
  });
  if (selectedCartonGsm !== null && selectedCartonGsm !== undefined) { params.set('carton_gsm', selectedCartonGsm); }
  params.set('area_ratio_percent', areaRatioPercent);
  if (count !== undefined && count !== null) { params.set('count', count); }
  return params;
};

// A plan's best (cheapest) score -- the SUM of its lines' bests, or null if any line has no score (a line that
// answered "cannot be made" makes the whole plan unmanufacturable). Matches the main listing's listBestScore.
const listBestScoreOf = (list, bestScoreByLine) => {
  let total = 0;
  for (const line of list.manufacturing_lines) {
    const score = bestScoreByLine[lineKey(line)];
    if (score == null) { return null; }
    total += score;
  }
  return total;
};

const StandardManufacturingLinesFallback = ({
  navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes,
  selectedCartonGsm, areaRatioPercent, count, cardsPerRow = 4, onSelectStandardLine,
}) => {
  const [listing, setListing] = useState(null);
  const [listingLoaded, setListingLoaded] = useState(false);
  // lineKey -> best score (number, or null for an unmanufacturable line). A key present means its answer landed;
  // the pass is READY once every distinct line has a key.
  const [bestScoreByLine, setBestScoreByLine] = useState({});
  const [scoresReady, setScoresReady] = useState(false);

  // Re-run whenever the section or its filters change (the parent re-searches and, still empty, re-renders this
  // with new props). The filter values are the effect's dependencies through this stable string.
  const filterKey = JSON.stringify({
    navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes,
    selectedCartonGsm, areaRatioPercent, count,
  });

  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined) { return undefined; }

    let cancelled = false;
    const controller = new AbortController();
    setListingLoaded(false);
    setListing(null);
    setBestScoreByLine({});
    setScoresReady(false);

    const filterParams = buildFilterParams({
      bobbinEnabled, disabledFilters, selectedTypes, selectedCartonGsm, areaRatioPercent, count,
    });
    const listingUrl = `/mcc/${window.user_quote_id}/manufacturing-line-lists/${navSectionIndex}?${filterParams.toString()}`;

    fetchJsonCached(listingUrl).then((data) => {
      if (cancelled) { return; }
      setListing(data || null);
      setListingLoaded(true);
      if (!data) { return; }

      // Every distinct line the standard listing offers -- scored once, by its stable key.
      const distinctKeys = [];
      const seen = new Set();
      allManufacturingLinesOf(data).forEach((line) => {
        if (line.sub_section_index == null) { return; }
        const key = lineKey(line);
        if (!seen.has(key)) { seen.add(key); distinctKeys.push(key); }
      });
      if (distinctKeys.length === 0) { setScoresReady(true); return; }

      const bestScoresUrl = `/mcc/${window.user_quote_id}/manufacturing-line-best-scores/${navSectionIndex}?${filterParams.toString()}`;
      pollSectionBestScores(bestScoresUrl, distinctKeys, {
        signal: controller.signal,
        onAnswer: (key, answer) => {
          if (cancelled) { return; }
          setBestScoreByLine((previous) => ({ ...previous, [key]: answer ? answer.best_score : null }));
        },
        // A score that could not be computed is recorded as null (unmanufacturable), the same em dash a real
        // "cannot be made" draws -- this read-only preview does not offer a retry.
        onLost: (key) => {
          if (cancelled) { return; }
          setBestScoreByLine((previous) => ({ ...previous, [key]: null }));
        },
      }).finally(() => {
        if (!cancelled) { setScoresReady(true); }
      });
    }).catch((error) => {
      if (cancelled) { return; }
      // The standard listing could not be fetched -- a real network/parse failure (fetchJsonCached resolves an
      // "unavailable" answer to null rather than rejecting, so this catch is the genuine break). Keep the signal
      // -- otherwise "the standard endpoint is broken for everyone" looks identical to "standard has no lines for
      // this section", and the whole fallback silently never appears. Consistent with the main listing's own
      // fetch-error logging (ManufacturingLineLists' solve/best-score console.error). Then let the loading line
      // clear so the offer resolves to nothing rather than spinning.
      console.error('Standard-lines fallback: could not fetch the standard manufacturing lines', error);
      setListingLoaded(true);
    });

    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterKey is the stable digest of every filter the
    // body reads, so it is the one honest trigger; listing the values individually would only restate it.
  }, [filterKey]);

  const lists = useMemo(
    () => manufacturingLineListsFromListing(listing, bestScoreByLine, TOP_N_PER_PART),
    [listing, bestScoreByLine],
  );

  const sortedLists = useMemo(() => {
    if (!scoresReady) { return lists; }
    return [...lists].sort((a, b) => {
      const scoreA = listBestScoreOf(a, bestScoreByLine);
      const scoreB = listBestScoreOf(b, bestScoreByLine);
      if (scoreA == null) { return scoreB == null ? 0 : 1; } // unmanufacturable plans sort last
      if (scoreB == null) { return -1; }
      return scoreA - scoreB; // lower score is better -- it is a cost
    });
  }, [lists, bestScoreByLine, scoresReady]);

  const maxMachineDimensionMm = useMemo(() => maxMachineDimensionOf(sortedLists), [sortedLists]);
  // The cheapest plan on screen -- what every card's star rating is a ratio to (best = 5).
  const bestListScore = useMemo(() => {
    if (!scoresReady) { return 0; }
    const scores = sortedLists.map((list) => listBestScoreOf(list, bestScoreByLine)).filter((score) => score != null);
    return scores.length ? minimumOf(scores) : 0;
  }, [sortedLists, bestScoreByLine, scoresReady]);

  // Still fetching the standard listing: a short line rather than a jump from nothing to a full grid.
  if (!listingLoaded) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--ink-faint, #8a94a3)', fontSize: '13px', padding: '20px' }}>
        {t('quote_builder.frank_standard_lines_fallback_loading')}
      </div>
    );
  }

  // The standard account makes nothing for this section either -- there is nothing to offer, so the finding above
  // stands alone.
  if (sortedLists.length === 0) { return null; }

  const renderCard = (list) => {
    const columnVisibility = columnVisibilityFor(list.manufacturing_lines);
    const drawnManufacturingLines = list.manufacturing_lines
      .map((manufacturingLine, index) => ({ manufacturingLine, index }))
      .filter(({ index }) => !columnVisibility[index].drawsNothing);

    const listBest = scoresReady ? listBestScoreOf(list, bestScoreByLine) : null;
    const rating = scoresReady ? starRatingForScore(listBest, bestListScore) : null;

    return (
      <div
        key={list.identifier}
        data-standard-manufacturing-line-list={list.identifier}
        style={{
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
          flex: `0 0 calc(${100 / cardsPerRow}% - 16px)`, maxWidth: `calc(${100 / cardsPerRow}% - 16px)`,
          margin: '8px', padding: '12px 8px',
          border: '1px solid #c4ccd6', borderRadius: '6px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '6px' }}>
          <div style={{ marginBottom: '2px', minHeight: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {scoresReady
              ? (rating != null ? <Stars rating={rating} /> : null)
              : <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonStars}`} />}
          </div>
          {/* The raw numeric score is an internal diagnostic (customers read the star rating above), gated on the
              show_score_details UserParam (window.isAdminShowDebug); the em dash ("cannot be made") always shows. */}
          {scoresReady && (listBest == null || window.isAdminShowDebug) && (
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: listBest == null ? '#c00' : '#2e7d32' }}>
              {listBest == null ? '—' : `Skor: ${listBest.toLocaleString()}`}
            </div>
          )}
        </div>
        {drawnManufacturingLines.map(({ manufacturingLine, index }) => (
          <ManufacturingLineCard
            key={`${manufacturingLine.identifier}-${index}`}
            manufacturingLine={manufacturingLine}
            maxMachineDimensionMm={maxMachineDimensionMm}
            sectionLabels={sectionLabelsFor(list, columnVisibility, index)}
            bare
            hidePricedSolutionCount
            hidePrintingColumn={columnVisibility[index].hidePrintingColumn}
            hideDieCuttingColumn={columnVisibility[index].hideDieCuttingColumn}
            hideCombinedColumn={columnVisibility[index].hideCombinedColumn}
            widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
          />
        ))}
        <button
          type="button"
          data-use-standard-manufacturing-line={list.identifier}
          className="dct-button dct-button--small dct-button--block dct-button--secondary"
          style={{ marginTop: 'auto', paddingTop: '10px' }}
          onClick={() => onSelectStandardLine(list.identifier)}
        >
          {t('quote_builder.frank_use_standard_line')}
        </button>
      </div>
    );
  };

  return (
    <div data-standard-manufacturing-lines-fallback>
      <div style={{ textAlign: 'center', color: 'var(--ink, #10141a)', fontSize: '14px', fontWeight: 600, margin: '8px 0' }}>
        {t('quote_builder.frank_standard_lines_fallback_heading')}
      </div>
      <div style={{ fontSize: '12px', color: 'var(--ink-soft, #4e5865)', textAlign: 'center', marginBottom: '10px' }}>
        {t('quote_builder.frank_standard_lines_fallback_subheading')}
      </div>
      <div style={{
        display: 'flex', flexWrap: 'wrap',
        justifyContent: sortedLists.length < cardsPerRow ? 'center' : 'flex-start', alignItems: 'stretch'
      }}>
        {sortedLists.map((list) => renderCard(list))}
      </div>
    </div>
  );
};

export default StandardManufacturingLinesFallback;
